import React, { useState } from "react";
import { Mail, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "../hooks/use-toast";

const ContactAndWaitlist = ({ onSubscribe, onContact }) => {
  const [wlEmail, setWlEmail] = useState("");
  const [wlLoading, setWlLoading] = useState(false);
  const [wlDone, setWlDone] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [ctLoading, setCtLoading] = useState(false);
  const [ctDone, setCtDone] = useState(false);

  const handleWL = async (e) => {
    e.preventDefault();
    if (!wlEmail || !wlEmail.includes("@")) {
      toast({ title: "Please enter a valid email" });
      return;
    }
    setWlLoading(true);
    try {
      await onSubscribe?.(wlEmail);
      setWlDone(true);
      setWlEmail("");
      toast({ title: "You're on the list ✨", description: "We'll reach out with early access soon." });
    } catch (err) {
      toast({ title: "Something went wrong", description: err.message || "Please try again." });
    } finally {
      setWlLoading(false);
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.message) {
      toast({ title: "Please fill out all fields correctly" });
      return;
    }
    setCtLoading(true);
    try {
      await onContact?.(form);
      setCtDone(true);
      setForm({ name: "", email: "", message: "" });
      toast({ title: "Message sent ✅", description: "We'll get back within 1–2 business days." });
    } catch (err) {
      toast({ title: "Something went wrong", description: err.message || "Please try again." });
    } finally {
      setCtLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-gradient-to-b from-[#f6f1e7] to-[#efe6d4]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Waitlist / Newsletter */}
          <div className="lg:col-span-2 rounded-3xl bg-emerald-950 text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-800/40 blur-2xl" />
            <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-amber-200/10 blur-2xl" />
            <div className="relative">
              <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 grid place-items-center mb-5">
                <Mail className="w-5 h-5 text-amber-200" />
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold leading-tight">
                Join the <span className="italic text-amber-200">insider list</span>
              </h3>
              <p className="mt-3 text-white/75">
                Get early access, travel guides, and product updates — no spam, ever.
              </p>

              {wlDone ? (
                <div className="mt-6 flex items-center gap-2 rounded-xl bg-white/10 border border-white/15 p-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                  <span className="text-sm">Welcome aboard — check your inbox.</span>
                </div>
              ) : (
                <form onSubmit={handleWL} className="mt-6 flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={wlEmail}
                    onChange={(e) => setWlEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12 rounded-xl focus-visible:ring-amber-200"
                  />
                  <Button
                    type="submit"
                    disabled={wlLoading}
                    className="h-12 rounded-xl bg-amber-200 hover:bg-amber-300 text-emerald-950 font-semibold px-6"
                  >
                    {wlLoading ? "Joining…" : "Join"}
                  </Button>
                </form>
              )}
              <div className="mt-6 text-xs text-white/60">By joining you agree to our privacy policy.</div>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-3 rounded-3xl bg-white border border-black/5 p-8 md:p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center">
                <MessageSquare className="w-5 h-5 text-emerald-800" />
              </div>
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-emerald-950">Get in touch</h3>
                <p className="text-emerald-950/60 text-sm">Questions, feedback, partnerships — we read every message.</p>
              </div>
            </div>

            {ctDone ? (
              <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                <span className="text-sm text-emerald-900">Thanks! Your message has landed safely.</span>
              </div>
            ) : (
              <form onSubmit={handleContact} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-emerald-950">Name</Label>
                    <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Amelia Rivers" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-emerald-950">Email</Label>
                    <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="msg" className="text-emerald-950">Message</Label>
                  <Textarea id="msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your trip or question…" className="mt-1.5 min-h-[120px] rounded-xl" />
                </div>
                <Button type="submit" disabled={ctLoading} className="rounded-full h-12 px-7 bg-emerald-900 hover:bg-emerald-800 text-white inline-flex items-center gap-2">
                  {ctLoading ? "Sending…" : (<>Send message <Send className="w-4 h-4" /></>)}
                </Button>
                <div className="text-xs text-emerald-950/50">Or email us at <a className="underline" href="mailto:support@splitara.app">support@splitara.app</a></div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactAndWaitlist;
